import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react'
import {
  useLazyQuery,
  useMutation,
  useQuery,
  useReactiveVar,
} from '@apollo/client'
import moment from 'moment'
import ReactMarkdown from 'react-markdown'
import classNames from 'classnames'

import { currentUserDetails } from '../api/apollo/cache/variables'
import { getCurrentAccountQRDetails } from '../api/apollo/graphql/company-client'
import {
  getCampaignLinkDashboardGraphClickData,
  getCampaignLinkDashboardGraphData,
  getCampaignLinkDashboardMeta,
  getCampaignLinkDashboardMetricClickData,
  getCampaignLinkDashboardMetricData,
  getCampaignLinkDashboardTable,
  listSavedLinkPerformanceReportsGQL,
  saveLinkPerformanceReport,
  updateSavedLinkPerformanceReport,
} from '../api/apollo/graphql/report-client'
import { getCampaignCodeGenerator } from '../api/apollo/graphql/track-create-client'
import { getMinCodesByAccount } from '../api/apollo/graphql/track-view-client'
import { getAccountDataSource } from '../api/apollo/graphql/workspace-client'
import { uploadAccountLogo } from '../api/apollo/REST/account-client'
import { DimensionOption, SelectedDimensions } from '../api/types'
import { getDimensionsFilterList } from '../api/utils'
import Stars from '../assets/star-cluster-yellow.svg'
import uplifter from '../assets/uplogo.png'
import AcquisitionActions from '../components/acquisition-actions'
import AnomalyDiff from '../components/anomaly-diff'
import { ButtonRow } from '../components/button-row'
import Button from '../components/button'
import { DropdownButtonItem } from '../components/button-dropdown'
import CollapsableFilters, {
  SupportMetricDropDown,
  CollapsableFiltersSummary,
  LinkFilter,
} from '../components/collapsable-filters'
import ConnectAnalyticsBlocker from '../components/connect-analytics-blocker'
import Manage from '../components/explain-manage'
import { ImgUploader } from '../components/img-uploader'
import { ClickEditInput } from '../components/input'
import Intro from '../components/intro'
import Layout from '../components/layout'
import Link from '../components/link'
import LinksTable from '../components/links-table'
import { LoadingLabel, Preloader } from '../components/loader'
import ManageReportsTabs, {
  SaveEditReportModal,
  getNewReportData,
  ManageReports,
} from '../components/manage-reports-tabs'
import MetricDataBanner from '../components/metric-data-banner'
import PerformanceReportDataSummary from '../components/performance-report-data-summary'
import PerformanceReportGraph from '../components/performance-report-graph'
import SelectBox from '../components/select-box'
import SiteWrapper from '../components/site-wrapper'
import Tag from '../components/tag'
import Tooltip from '../components/tooltip'
import {
  AvailableDimension,
  AvailableMetric,
} from '../context/reducers/graphql-reducer'
import {
  matchTypesDropDown,
  siteContainerWidth,
  performanceDates,
  dateFormatShort,
  messages,
} from '../core/variables'
import {
  getItemByKeyValue,
  getDefaultOption,
  sortData,
  hashCode,
  isAdminUser,
  getUrlQuery,
} from '../helpers'
import { setGranularityByDateRange } from '../helpers/report-module'
import useLogAction from '../hooks/useLogAction'
import useResize from '../hooks/useResize'
import useSwitchWorkspace from '../hooks/useSwitchWorkspace'
import { saveUserData, getUserData } from '../local-client/local-client'
import styles from '../styles/performance-report-page.module.scss'

const yesterday = new Date(Date.now())
yesterday.setDate(yesterday.getDate() - 1)

interface LogoProps {
  logoLink: string
  loading: boolean
}

export function Logo({ logoLink, loading }: LogoProps): React.ReactElement {
  if (loading) {
    return (
      <div
        className={classNames(styles.logoWrapper, {
          [styles.logoLoading]: loading,
        })}
      >
        <Preloader />
      </div>
    )
  }
  return (
    <div className={styles.logoWrapper}>
      <img src={logoLink} alt="Account logo" />
    </div>
  )
}

interface EditablePageTitleProps {
  setBreakdownDimension: Dispatch<SetStateAction<string>>
  dateRangeLabel: string
  breakdownDimension: string
  availableDimensions: AvailableDimension[]
  successMetricDisplayName: string
  loading: boolean
}

export function EditablePageTitle({
  setBreakdownDimension,
  dateRangeLabel,
  breakdownDimension,
  availableDimensions,
  successMetricDisplayName,
  loading,
}: EditablePageTitleProps) {
  const logAction = useLogAction()

  const useAvailableDimensions: {
    optionID: string
    optionName: string
    optionValue: string
  }[] = useMemo(() => {
    return availableDimensions
      ? [
          {
            optionID: '',
            optionName: 'No breakdown',
            optionValue: '',
          },
          ...sortData(
            availableDimensions.map((item) => {
              const { dimensionName, dimensionParameterID } = item
              return {
                optionID: dimensionParameterID,
                optionName: dimensionName,
                optionValue: dimensionName,
              }
            }),
            'optionName',
            true,
            null,
            true,
          ),
        ]
      : []
  }, [availableDimensions])

  if (loading || successMetricDisplayName === '') {
    return null
  }

  return (
    <h1 className={styles.graphTitle}>
      <span>
        {successMetricDisplayName} {dateRangeLabel}
        {breakdownDimension === '' ? '' : ` split by `}
      </span>
      <span
        className={classNames(styles.titleSelectWrapper, {
          [styles.hideBreakdown]: breakdownDimension === '',
        })}
      >
        <SelectBox
          id="breakdownDimension"
          className={styles.successMetricSelector}
          labelKey="optionName"
          valueKey="optionValue"
          placeholder="drilldown"
          value={
            useAvailableDimensions.find(
              (dim) =>
                breakdownDimension &&
                (dim.optionValue === breakdownDimension ||
                  dim.optionID === breakdownDimension),
            ) || null
          }
          options={useAvailableDimensions}
          onChange={(newValue) => {
            if (!newValue) return

            const { optionValue } = newValue

            const useDimension = matchTypesDropDown.find(
              (matchItem) => matchItem.value === optionValue,
            )
              ? optionValue
              : useAvailableDimensions.find(
                  (dim) => dim.optionValue === optionValue,
                )?.optionID || ''

            setBreakdownDimension(useDimension)

            logAction({
              variables: {
                action: 'graph-breakdown-updated',
                extra: optionValue,
                websiteSection: 'report',
                functionName: 'updateGraphBreakdown',
                pagePath: '/report/performance',
              },
            })
          }}
        />
      </span>
    </h1>
  )
}

export default function PerformanceReportPage() {
  const {
    userPermission,
    workspaceID,
    companySubscriptionLevel,
  } = useReactiveVar(currentUserDetails)

  const { switchWorkspace } = useSwitchWorkspace()

  const { data: dataSourceData } = useQuery(getAccountDataSource)
  const {
    data: campaignLinkDashboardMetaData,
    error: errorLoadingInterface,
  } = useQuery(getCampaignLinkDashboardMeta)
  const { data: campaignCodeGeneratorData } = useQuery(getCampaignCodeGenerator)
  const { data: logoData, refetch: refetchLogoDetails } = useQuery(
    getCurrentAccountQRDetails,
  )

  const [getUrlLinkDetails] = useLazyQuery(getMinCodesByAccount, {
    fetchPolicy: 'network-only',
  })
  const [
    getMetricClickData,
    {
      data: metricClickData,
      loading: metricClickDataLoading,
      error: metricClickDataError,
    },
  ] = useLazyQuery(getCampaignLinkDashboardMetricClickData, {
    fetchPolicy: 'network-only',
  })
  const [
    getMetricData,
    { data: metricData, loading: metricDataLoading, error: metricDataError },
  ] = useLazyQuery(getCampaignLinkDashboardMetricData, {
    fetchPolicy: 'network-only',
  })
  const [
    getGraphClickData,
    { data: fullGraphClickData, loading: loadingGraphClickData },
  ] = useLazyQuery(getCampaignLinkDashboardGraphClickData, {
    fetchPolicy: 'network-only',
  })
  const [
    getGraphData,
    { data: fullGraphData, loading: loadingGraphData },
  ] = useLazyQuery(getCampaignLinkDashboardGraphData, {
    fetchPolicy: 'network-only',
  })
  const [
    getTableData,
    { data: campaignLinkDashboardTableRowsData, loading: tableDataIsLoading },
  ] = useLazyQuery(getCampaignLinkDashboardTable, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const [saveReport] = useMutation(saveLinkPerformanceReport, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })
  const [updateSavedReport] = useMutation(updateSavedLinkPerformanceReport, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })

  const logAction = useLogAction()

  const [filterCheckComplete, setFilterCheckComplete] = useState(false)
  const [linkFilter, setLinkFilter] = useState<LinkFilter | null>(null)
  const [errorFilteringByLink, setErrorFilteringByLink] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [hash, setHash] = useState(0)
  const [filtersHaveChanged, setFiltersHaveChanged] = useState(false)
  const [currentReport, setCurrentReport] = useState<SavedReport | null>(null)
  const [loadGraphData, setLoadGraphData] = useState(false)
  const [showTopLinksTable, setShowTopLinksTable] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [matchType, setMatchType] = useState<MatchTypeProps>(
    matchTypesDropDown[0],
  )
  const [defaultSet, setDefaultSet] = useState(false)
  const [granularity, setGranularity] = useState<Granularity>('monthly')
  const [successMetric, setSuccessMetric] = useState('')
  const [successMetricsList, setSuccessMetricsList] = useState<any[]>([])
  const [breakdownDimension, setBreakdownDimension] = useState('')
  const [selectedDimensions, setSelectedDimensions] = useState<
    SelectedDimensions
  >({})
  const [manageReportsModalType, setManageReportsModalType] = useState<
    'manage' | 'save' | null
  >(null)

  const [width, setWidth] = useState(0)
  const [currentDate, setCurrentDate] = useState('thisYear')
  const [startDate, setStartDate] = useState(
    moment(yesterday).subtract(365, 'days').toDate(),
  )
  const [endDate, setEndDate] = useState(yesterday)
  const [titleValue, setTitleValue] = useState(messages.defaultReportTitle)

  const [showHighlight, setShowHighlight] = useState(true)

  const [dataBasedDateRange, setDataBasedDateRange] = useState('')

  // Check URL for a specific link ID/parameter to filter on
  useEffect(() => {
    if (!workspaceID) return

    const urlParams = getUrlQuery()

    const urlLinkID = urlParams.get('linkID')
    const parameterID = urlParams.get('parameterID')
    const parameterName = urlParams.get('parameterName')
    const parameterValue = urlParams.get('parameterValue')
    const urlWorkspaceID = urlParams.get('accountID')
    // Overrides swapping success metric to shortLinks if no metric data found on Track>View page
    const hideShortLinkMetric = urlParams.get('hideShortLinkMetric')

    // No need to add filters - allow data to load
    if (
      !urlParams ||
      (!urlLinkID && (!parameterID || !parameterValue || !parameterName))
    ) {
      setFilterCheckComplete(true)
      return
    }

    // Switch to correct account
    if (urlWorkspaceID && urlWorkspaceID !== workspaceID) {
      // TODO: Find a way to log actions for when users open a shared one-click report, but aren't the original user that created it
      // logAction({
      //   variables: {
      //     action: `view-one-click-report-shared`,
      //     functionName: 'pageLoad',
      //     pagePath: '/report/performance',
      //     websiteSection: 'report',
      //     extra: JSON.stringify({
      //       urlLinkID,
      //       parameterID,
      //       parameterName,
      //       parameterValue,
      //     }),
      //   },
      // })

      switchWorkspace(urlWorkspaceID)

      return
    }

    const createUrlLinkFilter = async () => {
      try {
        if (urlLinkID) {
          const { data } = await getUrlLinkDetails({
            variables: { codeIDList: [urlLinkID] },
          })

          if (!data) {
            throw new Error('Failed to fetch link data')
          }

          // codeDef is an array of the filters that must be applied
          const {
            codeDef,
            minGenDef,
            createdTime,
            shortLink,
            fullLink,
          } = data.minCodesByAccount

          const linkStartDate = new Date(
            moment.unix(parseInt(createdTime[0], 10)).format(),
          )

          setStartDate(linkStartDate)
          setCurrentDate('custom')
          setGranularity(setGranularityByDateRange(linkStartDate, yesterday))

          // Show short link click data instead of sessions data
          if (!hideShortLinkMetric && shortLink && shortLink[0]) {
            setSuccessMetric('shortLinkClicks')
          }

          setLinkFilter({
            id: urlLinkID,
            link: !shortLink ? fullLink[0] : shortLink[0] || fullLink[0],
          })

          // Update the selected dimensions to match
          const _selectedDimensions: SelectedDimensions = codeDef[0].reduce(
            (acc, curr, index) => {
              if (curr === '') return acc

              const dimName = minGenDef[index].paramName

              acc[dimName] = {
                dimensionParameterID: minGenDef[index].paramID,
                dimensionOptions: [curr],
              }

              return acc
            },
            {},
          )

          setSelectedDimensions(_selectedDimensions)
        } else if (parameterID && parameterName && parameterValue) {
          // Filter on parameter value
          setSelectedDimensions({
            [parameterName]: {
              dimensionParameterID: parameterID,
              dimensionOptions: [parameterValue],
            },
          })
        }
      } catch {
        setErrorFilteringByLink(true)
      } finally {
        setFilterCheckComplete(true)
      }
    }

    createUrlLinkFilter()
  }, [workspaceID])

  const dataSourceDescription = useMemo(() => {
    if (!dataSourceData || !dataSourceData.currentAccount.dataSource) return ''

    if (!dataSourceData.currentAccount.dataSource.connected) {
      return '**Metrics from:** *(Not connected)*'
    }

    const {
      adobeReportSuiteName,
      adobeSegmentName,
      ga4PropertyName,
      gaPropertyName,
      gaViewName,
    } = dataSourceData.currentAccount.dataSource

    switch (dataSourceData.currentAccount.dataSource.kind) {
      case 'GA_VIEW':
        return `**Metrics from:** Google Analytics (UA), property: **${gaPropertyName}**, view: **${gaViewName}**`
      case 'GA4_PROP':
        return `**Metrics from:** Google Analytics (GA4), property: **${ga4PropertyName}**`
      case 'AA_REPORT':
        return `Report suite: **${adobeReportSuiteName}**${
          adobeSegmentName ? ` *(segment applied: ${adobeSegmentName})*` : ''
        }`
      default:
        return '**Metrics from:** *(Not connected)*'
    }
  }, [dataSourceData])

  const paramsRequired = useMemo(() => {
    if (!campaignCodeGeneratorData) return []

    return campaignCodeGeneratorData.campaignCodeGenerator.paramDefs.map(
      ({ fieldID, required }) => ({ fieldID, required }),
    )
  }, [campaignCodeGeneratorData])

  const logoLink = useMemo(() => {
    if (!logoData || !logoData.currentAccount) return uplifter

    const useLogo = logoData.currentAccount.logoLink || uplifter

    return useLogo.indexOf('logos/PNG/uplogo.png') !== -1 ? uplifter : useLogo
  }, [logoData])

  useEffect(() => {
    if (workspaceID) {
      const savedData = getUserData(workspaceID)

      if (
        savedData &&
        Object.prototype.hasOwnProperty.call(savedData, 'showHighlight')
      ) {
        setShowHighlight(savedData.showHighlight)
      } else {
        setShowHighlight(true)
      }
    }
  }, [workspaceID])

  const updateShowHighlight = () => {
    saveUserData(
      {
        showHighlight: !showHighlight,
      },
      workspaceID,
    )
    setShowHighlight(!showHighlight)
  }

  const campaignLinkDashboardMeta = useMemo(() => {
    if (!campaignLinkDashboardMetaData) return null

    // ! Some shortlink metrics should be hidden from the data
    const useData = {
      ...campaignLinkDashboardMetaData.campaignLinkDashboardMeta,
      availableClickMetrics: campaignLinkDashboardMetaData
        .campaignLinkDashboardMeta.availableClickMetrics
        ? campaignLinkDashboardMetaData.campaignLinkDashboardMeta.availableClickMetrics.filter(
            (item) =>
              item.metricID !== 'shortLinkReturningClicksPercentage' &&
              item.metricID !== 'shortLinkReturningClicks' &&
              item.metricID !== 'shortLinkInitialClicks',
          )
        : null,
    }

    return useData
  }, [campaignLinkDashboardMetaData])

  const {
    availableDimensions,
    availableMetrics,
    availableClickMetrics,
  } = useMemo(() => {
    if (!campaignLinkDashboardMeta)
      return {
        availableDimensions: [],
        availableMetrics: [],
        availableClickMetrics: [],
      }

    return {
      availableDimensions: campaignLinkDashboardMeta.availableDimensions || [],
      availableMetrics: campaignLinkDashboardMeta.availableMetrics || [],
      availableClickMetrics:
        campaignLinkDashboardMeta.availableClickMetrics || [],
    }
  }, [campaignLinkDashboardMeta])

  const allAvailableMetrics = useMemo(() => {
    const useClickMetrics = availableClickMetrics || []
    if (!availableMetrics || !useClickMetrics) {
      return []
    }
    const res = [...availableMetrics, ...useClickMetrics].filter(
      (i) => i.setupComplete,
    )

    return res
  }, [availableMetrics, availableClickMetrics])

  const {
    successMetricDisplayName,
    successMetricTooltip,
    isUplifterClickMetric,
    units,
    increasePositive,
  } = useMemo(() => {
    const result: AvailableMetric | -1 = getItemByKeyValue(
      allAvailableMetrics,
      'metricID',
      successMetric,
    )

    return {
      increasePositive: result !== -1 ? result.increasePositive : true,
      successMetricDisplayName: result !== -1 ? result.displayName : '',
      successMetricTooltip: result !== -1 ? result.description : '',
      isUplifterClickMetric:
        result !== -1 ? result.isUplifterClickMetric : false,
      units: result !== -1 ? result.units : 'int',
    }
  }, [successMetric, matchType.value])

  const dashboardMetricData = useMemo(() => {
    if (
      metricClickData &&
      (isUplifterClickMetric || successMetric === 'shortLinkClicks')
    ) {
      return metricClickData.campaignLinkDashboardMetricDataClickData
    }

    if (metricData && !isUplifterClickMetric) {
      return metricData.campaignLinkDashboardMetricData
    }

    return null
  }, [metricData, metricClickData, isUplifterClickMetric, successMetric])

  const {
    graphData,
    dateRange: graphDateRange,
    dateTotalRows: tableTotals,
    graphType,
  } = useMemo(() => {
    if (
      fullGraphClickData &&
      (isUplifterClickMetric || successMetric === 'shortLinkClicks')
    ) {
      return fullGraphClickData.campaignLinkDashboardGraphClickData
    }

    if (fullGraphData && !isUplifterClickMetric) {
      return fullGraphData.campaignLinkDashboardGraph
    }

    return {
      graphData: [],
      dateRange: null,
      dateTotalRows: null,
      graphType: 'bar',
    }
  }, [fullGraphData, fullGraphClickData, isUplifterClickMetric, successMetric])

  const campaignLinkDashboardTableRows = useMemo(() => {
    if (!campaignLinkDashboardTableRowsData) return null

    // ! Some shortlink metrics should be hidden from the table
    const useData = {
      ...campaignLinkDashboardTableRowsData.campaignLinkDashboardTable,
      tableData: campaignLinkDashboardTableRowsData.campaignLinkDashboardTable.tableData.filter(
        (item) => {
          return (
            item.metricID !== 'shortLinkReturningClicksPercentage' &&
            item.metricID !== 'shortLinkReturningClicks' &&
            item.metricID !== 'shortLinkInitialClicks'
          )
        },
      ),
    }

    if (
      useData.campaignLinks &&
      useData.tableData &&
      Array.isArray(useData.campaignLinks) &&
      Array.isArray(useData.tableData)
    ) {
      const data = [...useData.campaignLinks].map((cL, i) => {
        const cols = {}
        useData.tableData.forEach((c) => {
          cols[c.metricID] = c.metricValue[i]
        })
        return {
          cL,
          ...cols,
        }
      })

      const keys = useData.tableData.map(
        ({ units: _units, metricID, helpText, metricName }) => {
          return {
            show: true,
            units: _units,
            metricID,
            helpText,
            metricName,
          }
        },
      )

      return { tableData: data, columnKeys: keys }
    }

    return null
  }, [campaignLinkDashboardTableRowsData])

  const matchTypeMerged = useMemo(() => {
    if (!availableDimensions || availableDimensions.length === 0) {
      return [...matchTypesDropDown]
    }

    return [
      ...matchTypesDropDown,
      ...sortData(
        availableDimensions.map((item) => {
          const { dimensionParameterID, dimensionName, helpText } = item
          return {
            name: dimensionName,
            shortName: dimensionName,
            value: dimensionName,
            tooltip: helpText,
            id: dimensionParameterID,
          }
        }),
        'name',
        true,
        null,
        true,
      ),
    ]
  }, [availableDimensions])

  const showDurationUnits = useMemo(() => {
    return (
      units === 'float' && successMetric.toLowerCase().indexOf('duration') > -1
    )
  }, [successMetric, units])

  useEffect(() => {
    if (campaignLinkDashboardMeta && workspaceID) {
      if (availableDimensions && availableDimensions.length > 0) {
        const urlFilterValue = new URLSearchParams(window.location.search).get(
          'compare',
        )

        const dimToMatch = urlFilterValue
          ? matchTypeMerged.find(
              (item) =>
                // @ts-ignore
                (item.id && item.id === urlFilterValue) ||
                item.value === urlFilterValue,
            )
          : null

        // Set initial filter type from URL
        if (dimToMatch) {
          setMatchType({
            ...dimToMatch,
            tooltip: dimToMatch.tooltip || '',
          })
        } else if (!defaultSet) {
          const mT = getDefaultOption(
            campaignLinkDashboardMeta,
            'onloadDefaultOptions',
            'matchType',
            'full',
          )

          if (matchType.id !== mT) {
            if (matchTypeMerged && matchTypeMerged.length > 3) {
              const useValue = matchTypeMerged.find(
                // @ts-ignore
                (item) => item.id === mT || item.value === mT,
              )

              if (useValue) {
                setMatchType({
                  value: useValue.value,
                  name: useValue.name,
                  shortName: useValue.shortName,
                  tooltip: '',
                  id: mT,
                })
              }
            }
          }
        }

        setDefaultSet(true)
      }

      if (
        breakdownDimension === '' &&
        availableDimensions &&
        availableDimensions.length > 0
      ) {
        setLoadGraphData(true)
      }

      if (successMetric === '' || typeof successMetric === 'undefined') {
        if (availableMetrics && availableMetrics.length > 0) {
          setSuccessMetric(
            availableMetrics.length > 0 ? availableMetrics[0].metricID : '',
          )
        } else if (availableClickMetrics && availableClickMetrics.length > 0) {
          setSuccessMetric(
            availableClickMetrics.length > 0
              ? availableClickMetrics[0].metricID
              : '',
          )
        }

        const useClickMetrics = availableClickMetrics || []

        let useList = [...availableMetrics, ...useClickMetrics]
          .filter((i) => i.setupComplete)
          .map((i) => i.metricID)

        if (workspaceID) {
          const savedData = getUserData(workspaceID)
          if (
            savedData &&
            Object.prototype.hasOwnProperty.call(
              savedData,
              'successMetricsList',
            )
          ) {
            useList = savedData.successMetricsList
          }
        }

        setSuccessMetricsList(useList)
      }
    }
  }, [
    // Allow the matchType to be reset when changing via nav
    window.location.search,
    campaignLinkDashboardMeta,
    workspaceID,
    availableDimensions,
    matchTypeMerged,
  ])

  const screenWidth = useResize()
  const graphWidth = useMemo(() => {
    let res: number

    if (width) {
      res = width
    } else if (screenWidth > 1260) {
      res = 1108
    } else if (screenWidth < siteContainerWidth) {
      res = screenWidth - 40
    } else if (screenWidth <= siteContainerWidth + 150) {
      res = screenWidth - 150
    } else {
      res = 960
    }

    return res - 34 - 40
  }, [width, screenWidth])

  const isMobile = screenWidth <= 768
  let offset = granularity === 'monthly' || granularity === 'quarterly' ? 0 : 40
  if (isMobile && offset > 0) {
    offset = 20
  }

  const getLinkDashboardData = useCallback(() => {
    let from = currentDate
    let to: string | null = null

    if (currentDate === 'custom') {
      from = moment(startDate).format('YYYY-MM-DD')
      to = moment(endDate).format('YYYY-MM-DD')
    }

    if (isUplifterClickMetric || successMetric === 'shortLinkClicks') {
      getMetricClickData({
        variables: {
          endDate: to,
          campaignCodeID: linkFilter ? linkFilter.id : undefined,
          filterList: getDimensionsFilterList(selectedDimensions),
          granularity,
          matchType: matchType.id ? matchType.id : matchType.value,
          selectedMetric: successMetric,
          startDate: from,
        },
      })

      getGraphClickData({
        variables: {
          endDate: to,
          campaignCodeID: linkFilter ? linkFilter.id : undefined,
          filterList: getDimensionsFilterList(selectedDimensions),
          granularity,
          matchType: matchType.id ? matchType.id : matchType.value,
          selectedMetric: successMetric,
          stackDimension: breakdownDimension || null,
          startDate: from,
        },
      })
    } else {
      getMetricData({
        variables: {
          endDate: to,
          campaignCodeID: linkFilter ? linkFilter.id : undefined,
          filterList: getDimensionsFilterList(selectedDimensions),
          granularity,
          matchType: matchType.id ? matchType.id : matchType.value,
          selectedMetric: successMetric,
          startDate: from,
        },
      })
      getGraphData({
        variables: {
          endDate: to,
          campaignCodeID: linkFilter ? linkFilter.id : undefined,
          filterList: getDimensionsFilterList(selectedDimensions),
          granularity,
          matchType: matchType.id ? matchType.id : matchType.value,
          selectedMetric: successMetric,
          stackDimension: breakdownDimension || null,
          startDate: from,
        },
      })
    }
  }, [
    successMetric,
    matchType,
    currentDate,
    startDate,
    endDate,
    selectedDimensions,
    granularity,
    isUplifterClickMetric,
    breakdownDimension,
    linkFilter,
  ])

  const _getTableData = useCallback(() => {
    let from = currentDate
    let to: string | null = null

    if (currentDate === 'custom') {
      from = moment(startDate).format('YYYY-MM-DD')
      to = moment(endDate).format('YYYY-MM-DD')
    }

    getTableData({
      variables: {
        endDate: to,
        campaignCodeID: linkFilter ? linkFilter.id : undefined,
        filterList: getDimensionsFilterList(selectedDimensions),
        matchType: matchType.id ? matchType.id : matchType.value,
        selectedMetric: successMetric,
        startDate: from,
      },
    })
  }, [
    matchType,
    selectedDimensions,
    successMetric,
    currentDate,
    startDate,
    endDate,
    linkFilter,
  ])

  const breakdownDimensionName = useMemo(() => {
    return (
      availableDimensions.find(
        (dim) =>
          dim.dimensionParameterID === breakdownDimension ||
          dim.dimensionName === breakdownDimension,
      )?.dimensionName || ''
    )
  }, [breakdownDimension])

  const breakdownDimensionID = useMemo(() => {
    return (
      availableDimensions.find(
        (dim) =>
          dim.dimensionParameterID === breakdownDimension ||
          dim.dimensionName === breakdownDimension,
      )?.dimensionParameterID || ''
    )
  }, [breakdownDimension])

  // Refetch graph and callout data on change
  useEffect(() => {
    if (loadGraphData && successMetric !== '' && filterCheckComplete) {
      getLinkDashboardData()
    }
  }, [
    selectedDimensions,
    successMetric,
    breakdownDimension,
    granularity,
    currentDate,
    startDate,
    endDate,
    matchType,
    loadGraphData,
    linkFilter,
    filterCheckComplete,
  ])

  // Refetch table data on change
  useEffect(() => {
    if (successMetric && filterCheckComplete) {
      _getTableData()
    }
  }, [
    matchType,
    selectedDimensions,
    successMetric,
    currentDate,
    startDate,
    endDate,
    linkFilter,
    filterCheckComplete,
  ])

  const updateHash = (overideValues = {}, forceSave = false) => {
    const currentHash = hashCode(
      JSON.stringify({
        ...{
          selectedDimensions,
          successMetric,
          breakdownDimension,
          currentDate,
          startDate,
          endDate,
          matchType,
          granularity,
          titleValue,
        },
        ...overideValues,
      }),
    )
    if (forceSave) {
      setFiltersHaveChanged(false)
      setHash(currentHash)
    } else {
      const hashUpdated = currentHash !== hash
      if (hashUpdated) {
        if (hash !== 0) {
          setFiltersHaveChanged(true)
        }
        setHash(currentHash)
      }
    }
  }

  useEffect(() => {
    if (defaultSet) {
      updateHash()
    }
  }, [
    currentReport,
    selectedDimensions,
    successMetric,
    breakdownDimension,
    currentDate,
    startDate,
    endDate,
    matchType,
    granularity,
    titleValue,
  ])

  const runReset = () => {
    if (currentReport !== null) {
      const foundMatchType = matchTypesDropDown.find(
        (matchItem) => matchItem.value === currentReport.matchType,
      )
        ? getItemByKeyValue(
            matchTypeMerged,
            'value',
            currentReport.matchType || undefined,
          )
        : getItemByKeyValue(
            matchTypeMerged,
            'id',
            currentReport.matchType || undefined,
          )

      if (foundMatchType) {
        setMatchType(foundMatchType)
      }

      setSuccessMetric(currentReport.selectedMetric)
      setBreakdownDimension(currentReport.stackDimension || '')
      setTitleValue(currentReport.reportTitle)
      const newSelectedDimensions: SelectedDimensions = {}

      if (
        currentReport &&
        Object.prototype.hasOwnProperty.call(currentReport, 'applyFilters') &&
        Array.isArray(currentReport.applyFilters)
      ) {
        currentReport.applyFilters.forEach((item) => {
          newSelectedDimensions[item.dimensionName] = {
            dimensionParameterID: item.dimensionParameterID,
            dimensionOptions: item.dimensionOptions,
          }
        })
      }

      setSelectedDimensions(newSelectedDimensions)
      if (
        Object.prototype.hasOwnProperty.call(currentReport, 'granularity') &&
        currentReport.granularity
      ) {
        setGranularity(currentReport.granularity)
      }

      if (currentReport.endDate === '') {
        setCurrentDate(currentReport.startDate)

        // TODO: implement correct range based on currentDate
        // value : custom | thisMonth | lastMonth | thisQuarter | lastQuarter | thisYear
        setStartDate(moment(yesterday).subtract(365, 'days').toDate())
        setEndDate(yesterday)
      } else {
        setCurrentDate('custom')
        setStartDate(moment(currentReport.startDate).toDate())
        setEndDate(moment(currentReport.endDate).toDate())
      }
    } else {
      setSelectedDimensions({})
      const urlFilterValue = new URLSearchParams(window.location.search).get(
        'compare',
      )

      // Set initial filter type from URL
      if (urlFilterValue) {
        const dimToMatch = matchTypeMerged.find(
          (item) =>
            // @ts-ignore
            (item.id && item.id === urlFilterValue) ||
            item.value === urlFilterValue,
        )

        if (dimToMatch) {
          setMatchType({
            ...dimToMatch,
            tooltip: dimToMatch.tooltip || '',
          })
        } else {
          setMatchType(matchTypesDropDown[0])
        }
      } else {
        setMatchType(matchTypesDropDown[0])
      }

      setBreakdownDimension('')
      setGranularity('monthly')
      setCurrentDate('thisYear')
      setSuccessMetric(
        availableMetrics.length > 0 ? availableMetrics[0].metricID : '',
      )
      setTitleValue(messages.defaultReportTitle)
    }
    setHash(0)
    setFiltersHaveChanged(false)
  }

  useEffect(() => {
    runReset()
  }, [currentReport])

  // Set date range shown in report
  useEffect(() => {
    if (tableDataIsLoading || !dashboardMetricData) return

    const { queryEndDate: eDate, queryStartDate: sDate } = dashboardMetricData
    const queryStartDate = sDate
    const queryEndDate = eDate

    if (!queryEndDate) {
      setDataBasedDateRange('')
      return
    }

    const start = moment(queryStartDate, 'YYYY-MM-DD').format(dateFormatShort)
    const end = moment(queryEndDate, 'YYYY-MM-DD').format(dateFormatShort)

    setDataBasedDateRange(`${start} - ${end}`)
  }, [dashboardMetricData, isUplifterClickMetric, tableDataIsLoading])

  const dateRangeLabel = useMemo(() => {
    const currentDateItem = getItemByKeyValue(
      performanceDates,
      'metricValue',
      currentDate,
    )
    if (
      currentDateItem &&
      currentDateItem.label &&
      currentDateItem.metricValue
    ) {
      if (currentDateItem.metricValue === 'custom') {
        const from = moment(startDate).format(dateFormatShort)
        const to = moment(endDate).format(dateFormatShort)
        return ` from ${from} to ${to}`
      }

      return ` over ${currentDateItem.label}`
    }
    return ''
  }, [currentDate, startDate, endDate])

  const graphDataIsLoading = useMemo(() => {
    return loadingGraphData || loadingGraphClickData
  }, [loadingGraphData, loadingGraphClickData])

  // Update success metric when shortlink clickthroughs is selected
  useEffect(() => {
    if (matchType.value === 'shortuplcode') {
      setSuccessMetric('shortLinkClicks')
    }
  }, [matchType])

  const acquisitionActionsButtons = useCallback(() => {
    return (
      <div className={styles.actionButtonsWrapper}>
        <AcquisitionActions
          onEditMetric={() => setShowModal(true)}
          className={styles.actionButton}
          granularity={granularity}
          selectedMetric={successMetric}
          stackDimension={breakdownDimension}
          currentDate={currentDate}
          startDate={startDate}
          endDate={endDate}
          selectedDimensions={selectedDimensions}
          reportTitle={currentReport?.reportName || titleValue}
          matchType={matchType.id ? matchType.id : matchType.value}
        >
          <DropdownButtonItem
            key="manageReports"
            onPress={() => {
              setManageReportsModalType('manage')
            }}
          >
            Manage reports
          </DropdownButtonItem>
          <DropdownButtonItem
            key="save"
            isDisabled={!filtersHaveChanged}
            onPress={async () => {
              if (currentReport === null) {
                setManageReportsModalType('save')
              } else {
                const filterList = Object.keys(selectedDimensions).map(
                  (key) => {
                    return {
                      dimensionParameterID:
                        selectedDimensions[key].dimensionParameterID,
                      dimensionName: key,
                      dimensionOptions:
                        selectedDimensions[key].dimensionOptions,
                    }
                  },
                )

                await updateSavedReport({
                  variables: {
                    ...currentReport,
                    filterList,
                    startDate:
                      currentDate === 'custom'
                        ? moment(startDate).format('YYYY-MM-DD')
                        : currentDate,
                    endDate:
                      currentDate === 'custom'
                        ? moment(endDate).format('YYYY-MM-DD')
                        : '',
                    matchType: Object.prototype.hasOwnProperty.call(
                      matchType,
                      'id',
                    )
                      ? matchType.id
                      : matchType.value,
                    stackDimension: breakdownDimension,
                    selectedMetric: successMetric,
                    granularity,
                  },
                })
              }
            }}
          >
            {currentReport === null ? 'Save report' : 'Save changes'}
          </DropdownButtonItem>
        </AcquisitionActions>
      </div>
    )
  }, [
    granularity,
    successMetric,
    breakdownDimension,
    currentDate,
    startDate,
    endDate,
    selectedDimensions,
    filtersHaveChanged,
    currentReport,
    matchType,
  ])

  const defaultSortAsc = useMemo(() => {
    if (showTopLinksTable) {
      return !increasePositive
    }
    return increasePositive
  }, [showTopLinksTable, increasePositive])

  // Report page should show if workspace has shortlink clickthrough data
  // Even if they are not connected to analytics
  const hasClickData = useMemo(() => {
    return !(
      (availableClickMetrics === null || availableClickMetrics.length === 0) &&
      availableMetrics.length === 0
    )
  }, [availableClickMetrics, availableMetrics])

  if (
    (companySubscriptionLevel === 'preSetup' ||
      (dataSourceData &&
        !dataSourceData.currentAccount.dataSource?.connected)) &&
    campaignLinkDashboardMetaData &&
    !hasClickData
  ) {
    return (
      <SiteWrapper>
        <Layout width={1600}>
          <Intro />
          <ConnectAnalyticsBlocker
            reconnect={
              dataSourceData?.currentAccount.dataSource?.kind !== 'BLANK'
            }
          />
        </Layout>
      </SiteWrapper>
    )
  }

  const pageTitle = 'Performance report'

  if (errorLoadingInterface) {
    return (
      <SiteWrapper>
        <Layout width={1600}>
          <Intro title={pageTitle}>
            <p>We are having trouble loading your dashboard data...</p>
          </Intro>
        </Layout>
      </SiteWrapper>
    )
  }

  const onResetFilters = () => {
    setSelectedDimensions({})
  }

  const onResetReport = () => {
    runReset()
  }

  const onChangeFilters = (val: DimensionOption, item: any) => {
    const dimension = {
      ...selectedDimensions,
    }

    delete dimension[item.dimensionName]
    // when an array is empty, the filter should get removed
    if (
      Array.isArray(val.dimensionOptions) &&
      val.dimensionOptions.length !== 0
    ) {
      dimension[item.dimensionName] = val
    }

    setSelectedDimensions({
      ...dimension,
    })
  }

  return (
    <SiteWrapper>
      <Layout width={1600}>
        <div
          ref={(element) => {
            if (element) {
              const container = element.getBoundingClientRect()
              setWidth(container.width - 42)
            }
          }}
        >
          <Intro title={pageTitle} className={styles.intro}>
            <p>Create custom reports which compare marketing performance.</p>
            {dataSourceData &&
              !dataSourceData.currentAccount.dataSource?.connected && (
                <Tag inline icon={Stars}>
                  <p>
                    <Link href="/connect">Connect your analytics platform</Link>{' '}
                    to compare metrics for each campaign link.
                  </p>
                </Tag>
              )}
            {errorFilteringByLink && (
              <p className={styles.errorMsg}>
                Error creating filtered report for selected link.
              </p>
            )}
          </Intro>
          <CollapsableFilters
            accountID={workspaceID}
            isMobile={isMobile}
            data={availableDimensions}
            selectedDimensions={selectedDimensions}
            paramsRequired={paramsRequired}
            onResetFilters={onResetFilters}
            onChange={(val: DimensionOption, item: any) => {
              onChangeFilters(val, item)
            }}
          >
            <PerformanceReportDataSummary
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              matchType={matchType}
              setMatchType={setMatchType}
              matchTypeMerged={matchTypeMerged}
              availableDimensions={availableDimensions}
              setDefaultSet={setDefaultSet}
              setBreakdownDimension={setBreakdownDimension}
              granularity={granularity}
              setGranularity={setGranularity}
              successMetric={successMetric}
              setSuccessMetric={setSuccessMetric}
              allAvailableMetrics={allAvailableMetrics}
              setShowTopLinksTable={setShowTopLinksTable}
              setShowModal={setShowModal}
            />
          </CollapsableFilters>
          <ManageReportsTabs
            defaultReportName={titleValue}
            granularity={granularity}
            actionButtons={acquisitionActionsButtons()}
            onSaveReport={() => {
              setFiltersHaveChanged(false)
              setShowCreateModal(false)
              setCurrentReport(null)
              updateHash(
                {
                  titleValue: messages.defaultReportTitle,
                },
                true,
              )
              setTitleValue(messages.defaultReportTitle)
            }}
            loading={availableDimensions.length === 0}
            showSaveButton={filtersHaveChanged}
            onResetReport={onResetReport}
            selectedDimensions={selectedDimensions}
            currentDate={currentDate}
            startDate={startDate}
            endDate={endDate}
            matchType={matchType}
            breakdownDimension={breakdownDimensionID}
            successMetric={successMetric}
            onChangeReport={(selectedReport) =>
              setCurrentReport(selectedReport)
            }
          />
          <div className={styles.paper}>
            <div className={styles.paperHeader}>
              {isMobile && (
                <div className={styles.logoWrapper}>
                  {/* Deliberately excludes logo uploader - avoid showing 'replace' button */}
                  <Logo logoLink={logoLink} loading={!logoData} />
                </div>
              )}
              <div className={styles.paperHeaderContent}>
                <ButtonRow className={styles.headerButtonRow}>
                  <div className={styles.editTitleWrapper}>
                    <ClickEditInput
                      key={titleValue}
                      id="title"
                      name="title"
                      value={titleValue}
                      className={styles.editTitle}
                      onChange={(newValue: string) => {
                        if (newValue !== titleValue) {
                          setTitleValue(newValue)
                          if (currentReport !== null) {
                            updateSavedReport({
                              variables: {
                                savedReportID: currentReport.savedReportID,
                                reportName: newValue,
                                reportTitle: newValue,
                              },
                            })

                            updateHash(
                              {
                                titleValue: newValue,
                              },
                              true,
                            )
                          } else {
                            setShowCreateModal(true)
                          }

                          logAction({
                            variables: {
                              action: 'link-performance-title-updated',
                              extra: newValue,
                              websiteSection: 'report',
                              functionName: 'updateLinkPerformanceTitle',
                              pagePath: '/report/performance',
                            },
                          })
                        }
                      }}
                    />
                  </div>
                </ButtonRow>
                <p className={styles.dateRangePar}>
                  <strong>Date range:</strong>{' '}
                  {dataBasedDateRange ? (
                    `${dataBasedDateRange}`
                  ) : (
                    <LoadingLabel label="Loading" />
                  )}
                </p>
                {dataSourceDescription ? (
                  <ReactMarkdown source={dataSourceDescription} />
                ) : (
                  <p className={styles.dateRangePar}>
                    <strong>Metrics from:</strong>{' '}
                    <LoadingLabel label="Loading" />
                  </p>
                )}
                {!(successMetricDisplayName && successMetricTooltip) && (
                  <p>
                    <strong>Key metric:</strong>{' '}
                    <LoadingLabel label="Loading" />
                  </p>
                )}
                {successMetricDisplayName && successMetricTooltip && (
                  <p>
                    <strong>{successMetricDisplayName}:</strong>{' '}
                    <span>{successMetricTooltip}</span>
                  </p>
                )}
                <CollapsableFiltersSummary
                  data={availableDimensions || []}
                  selectedDimensions={selectedDimensions}
                  linkFilter={linkFilter}
                  setLinkFilter={setLinkFilter}
                  onChange={(val: DimensionOption, item: any) => {
                    onChangeFilters(val, item)
                  }}
                />
              </div>
              {!isMobile && (
                <div className={styles.logoWrapper}>
                  {isAdminUser(userPermission) && (
                    <ImgUploader
                      logoLink={logoLink}
                      imgType="logo"
                      uploadFn={async (file: File) => {
                        const res = await uploadAccountLogo({
                          file,
                        })

                        if (res === true) {
                          logAction({
                            variables: {
                              action: 'company-logo-changed',
                              extra: '',
                              websiteSection: 'settings',
                              functionName: 'updateCompanyLogo',
                              pagePath: '/report/performance',
                            },
                          })

                          refetchLogoDetails()

                          return true
                        }
                        return false
                      }}
                      className={styles.reportLogo}
                    />
                  )}
                  {!isAdminUser(userPermission) && (
                    <Logo logoLink={logoLink} loading={!logoData} />
                  )}
                </div>
              )}
            </div>
            <MetricDataBanner
              loading={
                isUplifterClickMetric
                  ? metricClickDataLoading
                  : metricDataLoading
              }
              error={
                isUplifterClickMetric
                  ? !!metricClickDataError
                  : !!metricDataError
              }
              granularity={granularity}
              dateRange={dataBasedDateRange}
              data={dashboardMetricData}
              successMetric={successMetricDisplayName}
              units={units}
              showDurationUnits={showDurationUnits}
            />
            <div className={styles.graphWrapper}>
              <PerformanceReportGraph
                title={
                  breakdownDimensionName === ''
                    ? 'No breakdown'
                    : breakdownDimensionName
                }
                loading={graphDataIsLoading}
                units={units || 'float'}
                showDurationUnits={showDurationUnits}
                graphWidth={graphWidth}
                offset={offset}
                granularity={granularity}
                isMobile={isMobile}
                graphDateRange={graphDateRange}
                graphData={graphData}
                totalsData={tableTotals}
                graphType={graphType}
                availableDimensions={availableDimensions}
                breakdownDimension={breakdownDimensionName}
                width={width}
                successMetricDisplayName={successMetricDisplayName}
                setDataBasedDateRange={setDataBasedDateRange}
              >
                <EditablePageTitle
                  loading={graphDataIsLoading}
                  successMetricDisplayName={successMetricDisplayName}
                  setBreakdownDimension={setBreakdownDimension}
                  dateRangeLabel={dateRangeLabel}
                  breakdownDimension={breakdownDimensionName}
                  availableDimensions={availableDimensions}
                />
              </PerformanceReportGraph>
            </div>
            <div>
              <SupportMetricDropDown
                accountID={workspaceID}
                successMetricsList={successMetricsList}
                allAvailableMetrics={allAvailableMetrics}
                setSuccessMetricsList={setSuccessMetricsList}
              />
            </div>
            <div className={styles.tableWrapper}>
              <LinksTable
                key={`links-table-${showTopLinksTable}${defaultSortAsc}`}
                campaignLinkDashboardTableRows={campaignLinkDashboardTableRows}
                defaultSortAsc={defaultSortAsc}
                increasePositive={increasePositive}
                onChangeSuccessMetric={(_, orderAsc) => {
                  if (increasePositive) {
                    setShowTopLinksTable(!orderAsc)
                  } else {
                    setShowTopLinksTable(orderAsc)
                  }
                }}
                defaultSortKey={successMetric}
                matchType={matchType.value}
                currentMetricID={successMetric}
                showColumns={
                  successMetricsList.length === 0
                    ? [successMetric]
                    : successMetricsList
                }
                loading={!defaultSet || tableDataIsLoading}
                showHighlight={showHighlight}
                updateShowHighlight={updateShowHighlight}
              >
                <Button
                  variant="text"
                  className={styles.headerButton}
                  onPress={() => setShowTopLinksTable(!showTopLinksTable)}
                >
                  <h2 className={styles.tableTitle}>
                    <AnomalyDiff
                      arrowOnly
                      largeIcon
                      compact
                      actual={showTopLinksTable ? 2 : 1}
                      expected={showTopLinksTable ? 1 : 2}
                    />
                    <span className={styles.titleText}>
                      {showTopLinksTable
                        ? `Top performing ${matchType.shortName}`
                        : `Underperforming ${matchType.shortName}`}
                    </span>
                  </h2>
                </Button>
                <Tooltip
                  id="table-header-tooltip"
                  useIcon
                  tooltipIconClassName={styles.titleTooltip}
                  tooltipPosition="bottom"
                  tooltipMessage={
                    showTopLinksTable
                      ? `The top 50% of campaign ${matchType.shortName}, ranked by ${successMetricDisplayName}, based on your chosen filters.`
                      : `The bottom 50% of campaign ${matchType.shortName}, ranked by ${successMetricDisplayName}, based on your chosen filters.`
                  }
                />
              </LinksTable>
            </div>
          </div>
        </div>
        <Manage active={showModal} isReport toggleActive={setShowModal} />
        {showCreateModal && (
          <SaveEditReportModal
            item={currentReport}
            toggleActive={setShowCreateModal}
            defaultReportName={titleValue}
            onSuccess={({ reportName, availableTo }) => {
              if (currentReport === null) {
                const newReportData = getNewReportData({
                  selectedDimensions,
                  currentDate,
                  startDate,
                  endDate,
                  matchType,
                  availableTo,
                  breakdownDimension,
                  successMetric,
                  reportName,
                  granularity,
                })

                saveReport({
                  variables: newReportData,
                })
              }
              setShowCreateModal(false)
              setCurrentReport(null)
              updateHash(
                {
                  titleValue: messages.defaultReportTitle,
                },
                true,
              )
              setTitleValue(messages.defaultReportTitle)
            }}
          />
        )}
        <ManageReports
          showEdit={manageReportsModalType === 'manage'}
          showSave={manageReportsModalType === 'save'}
          setManageReportsModalType={setManageReportsModalType}
          granularity={granularity}
          selectedDimensions={selectedDimensions}
          currentDate={currentDate}
          startDate={startDate}
          endDate={endDate}
          matchType={matchType}
          breakdownDimension={breakdownDimensionID}
          successMetric={successMetric}
        />
      </Layout>
    </SiteWrapper>
  )
}
